import { forwardRef } from 'react';
import Card from '../../../components/card/Card';
import H from '../../../components/texts/h/H';
import P from '../../../components/texts/p/P';
import Table from '../../../components/table/table';
import CheckLightResume from '../CheckLight/ChecklightResume';
import {
  balanceInfo,
  clientData,
  documentData,
  riskData,
  StandardizedBalanceData,
  ReferrerName,
  applicationData,
} from '../../../types/interfaces';
import { economicActivities } from '../../../mocks/EconomicActivities';
import { ivaStatuses } from '../../../mocks/IvaStatuses';
import { legalEntities } from '../../../mocks/LegalEntities';
import { otherLegalEntities } from '../../../mocks/OtherLegalEntities';
import { CURRENCY_CODES } from '../../../utils/Constants';
import { formatCurrency } from '../../../utils/Masks';
import Icon from '../../../components/icon/Icon';

interface RiskAnalysisPdfProps {
  clientData: clientData;
  riskData: riskData[] | undefined;
  balanceData?: balanceInfo | StandardizedBalanceData;
  postBalanceData?: any;
  debts?: any;
  documents?: documentData[];
  cuit?: string;
  referrerNames?: Array<{ code: string; name: string }>;
  officers?: Array<{ code: string; label: string }>;
  productLines?: Array<{ code: string; label: string }>;
}

const RiskAnalysisPdf = forwardRef<HTMLDivElement, RiskAnalysisPdfProps>(
  ({ clientData, riskData, balanceData, postBalanceData, debts, documents, cuit, referrerNames = [], officers = [], productLines = [] }, ref) => {
    // Helper functions for transforming codes to descriptions
    const getActivityDescription = (activityCode: string) => {
      const activity = economicActivities.find(a => String(a.code) === String(activityCode));
      return activity ? activity.label : activityCode || '—';
    };

    const getIvaStatusDescription = (ivaCode: string) => {
      const ivaStatus = ivaStatuses.find(status => String(status.code) === String(ivaCode));
      return ivaStatus ? ivaStatus.label : ivaCode || '—';
    };

    const getReferrerNameLabel = (referrerName: string | ReferrerName | undefined): string => {
      if (!referrerName) return '—';
      if (typeof referrerName === 'string') {
        const found = referrerNames.find(rn => rn.code === referrerName);
        return found ? found.name : referrerName || '—';
      }
      return referrerName.name || '—';
    };

    const getOfficerLabel = (officerCode: string | undefined): string => {
      if (!officerCode) return '—';
      const found = officers.find(o => o.code === officerCode);
      return found ? found.label : officerCode || '—';
    };

    const formatCurrencyValue = (value: number | undefined): string => {
      if (value === undefined || value === null) return '—';
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const partnersColumns = [
      { name: 'Nombre', selector: (row: any) => row.first_name || '—', grow: 1 },
      { name: 'Documento', selector: (row: any) => row.ci || '—', grow: 0.5 },
      { name: 'Cargo', selector: (row: any) => row.position || '—', grow: 0.4 },
      { name: 'Participación', selector: (row: any) => row.ownership_interest || '—', grow: 0.5 },
      { name: 'Bienes', selector: (row: any) => row.assets || '—', grow: 0.8 },
      { name: 'Valor', selector: (row: any) => row.value ? formatCurrencyValue(row.value) : '—', grow: 0.7 },
    ];

    const partnersData = clientData?.corporate_structure || [];

    // Helper to get product line label
    const getProductLineLabel = (productLineCode: string): string => {
      const found = productLines.find(pl => pl.code === productLineCode);
      return found ? found.label : productLineCode || '—';
    };

    // Products table columns
    const productsColumns = [
      { 
        name: 'Producto', 
        selector: (row: applicationData) => getProductLineLabel(row.product_line_code), 
        grow: 1 
      },
      { 
        name: 'Moneda', 
        selector: (row: applicationData) => row.amount?.currency_code || '—', 
        grow: 0.5 
      },
      { 
        name: 'Monto', 
        selector: (row: applicationData) => `$ ${formatCurrency(row.amount?.value ?? 0, true)}`, 
        grow: 0.8,
        right: true 
      },
    ];

    // Calculate totals for ARS and USD
    const productsData = clientData?.products || [];
    const totals = productsData.reduce(
      (acc, product: applicationData) => {
        const value = Number(product.amount?.value || 0);
        const currencyCode = product.amount?.currency_code;
        if (currencyCode === CURRENCY_CODES.ars || currencyCode === 'ARS') {
          acc.totalArs += value;
        } else if (currencyCode === CURRENCY_CODES.usd || currencyCode === 'USD') {
          acc.totalUsd += value;
        }
        return acc;
      },
      { totalArs: 0, totalUsd: 0 }
    );

    return (
      <div
        ref={ref}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '1200px',
          background: '#fff',
          padding: '24px',
        }}
      >
        <Card spacing={'3'}>
          <Card variant='neutral' modifier='inverted' spacing={'2'}>
            <H type='2' variant='neutral-1' withMargin={false} weight='bold' modifier='strong'>
              Datos Generales
            </H>
          </Card>

          {/* Company Name */}
          <Card margin={{ top: '3' }}>
            <P type='1' weight='bold' variant='neutral'>
              {clientData?.basic_data?.company_name || '—'}
            </P>
          </Card>

          {/* Row 1: Personería, Fecha de constitución, Condición frente al IVA */}
          <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Personería:</P>
              <P type='2' variant='neutral'>{clientData?.basic_data?.legal_entity || '—'}</P>
            </Card>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Fecha de constitución:</P>
              <P type='2' variant='neutral'>{clientData?.basic_data?.date_incorporation || '—'}</P>
            </Card>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Condición frente al IVA:</P>
              <P type='2' variant='neutral'>{getIvaStatusDescription(clientData?.basic_data?.iva_status)}</P>
            </Card>
          </Card>

          {/* Row 2: Actividad económica */}
          <Card display='flex' gap='4px' width='100%' margin={{ top: '2' }}>
            <P type='2' variant='neutral' weight='bold'>Actividad económica:</P>
            <P type='2' variant='neutral'>{getActivityDescription(clientData?.basic_data?.activity)}</P>
          </Card>

          {/* Row 3: Domicilio fiscal, Localidad, Provincia */}
          <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Domicilio fiscal:</P>
              <P type='2' variant='neutral'>{clientData?.basic_data?.company_address || '—'}</P>
            </Card>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Localidad:</P>
              <P type='2' variant='neutral'>{clientData?.basic_data?.city || '—'}</P>
            </Card>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Provincia:</P>
              <P type='2' variant='neutral'>{clientData?.basic_data?.state || '—'}</P>
            </Card>
          </Card>

          {/* Row 4: Teléfono, Correo electrónico, Referido */}
          <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Teléfono:</P>
              <P type='2' variant='neutral'>{clientData?.basic_data?.phone_number || '—'}</P>
            </Card>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Correo electrónico:</P>
              <P type='2' variant='neutral'>{clientData?.basic_data?.email || '—'}</P>
            </Card>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Referido:</P>
              <P type='2' variant='neutral'>{getReferrerNameLabel(clientData?.referrer_name)}</P>
            </Card>
          </Card>

          {/* Row 5: Oficial, Comentarios */}
          <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Oficial:</P>
              <P type='2' variant='neutral'>{getOfficerLabel(clientData?.officer)}</P>
            </Card>
            <Card display='flex' gap='4px' width='60%'>
              <P type='2' variant='neutral' weight='bold'>Comentarios:</P>
              <P type='2' variant='neutral'>{clientData?.extra_comments || '—'}</P>
            </Card>
          </Card>

          {/* Row 6: Motivo */}
          <Card display='flex' gap='4px' margin={{ top: '2' }}>
            <P type='2' variant='neutral' weight='bold'>Motivo:</P>
            <P type='2' variant='neutral'>{clientData?.reason || '—'}</P>
          </Card>

          {/* Row 7: Reseñas */}
          <Card display='flex' gap='4px' margin={{ top: '2' }}>
            <P type='2' variant='neutral' weight='bold'>Reseñas:</P>
            <P type='2' variant='neutral'>{clientData?.review || '—'}</P>
          </Card>

          {/* Solicitud Section - solo si hay productos */}
          {productsData.length > 0 && (
            <Card margin={{ top: '4' }}>
              <Card variant='success' modifier='weak' spacing={'2'}>
                <H type='3' variant='neutral-1' weight='bold' modifier='strong' withMargin={false}>
                  Solicitud
                </H>
              </Card>
              
              {/* Motivo */}
              <Card display='flex' gap='4px' margin={{ top: '2' }}>
                <P type='2' variant='neutral' weight='bold'>Motivo:</P>
                <P type='2' variant='neutral'>{clientData?.reason || '—'}</P>
              </Card>

              {/* Tabla de productos y resumen de montos */}
              <Card display='flex' justifyContent='space-between' alignItems='flex-start' margin={{ top: '2' }} gap='20px'>
                {/* Tabla productos */}
                <Card width='60%'>
                  <Table
                    columns={productsColumns}
                    data={productsData}
                    tableData={{ size: productsData.length, totalElements: productsData.length }}
                    variant='weak'
                  />
                </Card>
                
                {/* Resumen ARS/USD */}
                <Card 
                  width='35%' 
                  border={true} 
                  variant='success' 
                  modifier='weak' 
                  spacing={'2'}
                  display='flex'
                  flexDirection='column'
                  justifyContent='center'
                  alignItems='center'
                  gap='8px'
                >
                  <H type='2' withMargin={false} weight='bold' variant='success' align='center'>
                    {CURRENCY_CODES.ars} {formatCurrency(totals.totalArs)}
                  </H>
                  <H type='2' withMargin={false} weight='bold' variant='success' align='center'>
                    {CURRENCY_CODES.usd} {formatCurrency(totals.totalUsd)}
                  </H>
                </Card>
              </Card>
            </Card>
          )}

          {/* Partners Section */}
          {partnersData.length > 0 && (
            <Card margin={{ top: '4' }}>
              <Card variant='success' modifier='weak' spacing={'2'}>
                <H type='3' variant='neutral-1' weight='bold' modifier='strong' withMargin={false}>
                  Socios
                </H>
              </Card>
              <Card margin={{ top: '2' }}>
                <Table
                  columns={partnersColumns}
                  data={partnersData}
                  tableData={{ size: partnersData.length, totalElements: partnersData.length }}
                  variant='weak'
                />
              </Card>
            </Card>
          )}

          {/* Documentation Section */}
          <Card margin={{ top: '4' }}>
            <Card variant='success' modifier='weak' spacing={'2'}>
              <H type='3' variant='neutral-1' weight='bold' modifier='strong' withMargin={false}>
                Documentación
              </H>
            </Card>
            <Card margin={{ top: '3' }}>
              <P type='1' weight='bold' variant='neutral'>
                Documentación
              </P>
              <Card
                display='flex'
                flexWrap='wrap'
                gap='20px'
                justifyContent='space-between'
                margin={{ top: '3' }}
              >
                {legalEntities
                  .find((item) => item.label === clientData?.basic_data?.legal_entity)
                  ?.requiredDocuments.map((document, index) => {
                    const isDocumentUploaded = clientData?.files?.find(
                      (item) => item?.type === document.id
                    )?.is_basic_data;
                    
                    return (
                      <Card
                        key={index}
                        width='48%'
                        display='flex'
                        height='auto'
                        alignItems='center'
                        gap='16px'
                      >
                        {isDocumentUploaded ? (
                          <Icon name='done_outline' variant='success' />
                        ) : (
                          <Icon name='cancel_outline' variant='danger' />
                        )}
                        <Card width='-webkit-fill-available'>
                          <P 
                            withMargin={false} 
                            type='2' 
                            variant='neutral' 
                            align='left'
                            modifier={isDocumentUploaded ? undefined : 'weak'}
                          >
                            {document.value}
                          </P>
                        </Card>
                        {isDocumentUploaded && (
                          <Card 
                            display='flex' 
                            justifyContent='center' 
                            width='60px'
                            border={true}
                            spacing={'1'}
                          >
                            <Icon
                              name='view_outline'
                              height={32}
                              variant='neutral'
                            />
                          </Card>
                        )}
                      </Card>
                    );
                  })}
              </Card>
              
              {/* Documentación extra */}
              {clientData?.files?.some((doc) => !doc.is_basic_data) && (
                <Card margin={{ top: '4' }}>
                  <P type='1' weight='bold' variant='neutral'>
                    Documentación extra
                  </P>
                  <Card
                    display='flex'
                    flexWrap='wrap'
                    gap='20px'
                    justifyContent='space-between'
                    margin={{ top: '2' }}
                  >
                    {clientData?.files?.map(
                      (document, index) =>
                        !document.is_basic_data && (
                          <Card
                            key={index}
                            width='48%'
                            display='flex'
                            alignItems='center'
                            gap='16px'
                          >
                            <Icon name='done_outline' variant='success' />
                            <Card width='-webkit-fill-available'>
                              <P
                                withMargin={false}
                                type='2'
                                variant='neutral'
                                align='left'
                              >
                                {otherLegalEntities.find(
                                  (item) => item.id === document.type
                                )?.label || document.type}
                              </P>
                            </Card>
                            <Card 
                              display='flex' 
                              justifyContent='center' 
                              width='60px'
                              border={true}
                              spacing={'1'}
                            >
                              <Icon
                                name='view_outline'
                                height={32}
                                variant='neutral'
                              />
                            </Card>
                          </Card>
                        )
                    )}
                  </Card>
                </Card>
              )}
            </Card>
          </Card>
        </Card>

        <Card margin={{ top: '4' }}>
          <CheckLightResume
            data={riskData}
            showBalanceInfo={true}
            isLoading={false}
            handleRiskData={() => { }}
            handleRiskDataAndDebts={() => { }}
            riskCodes={[]}
            debts={debts}
            documents={documents}
            cuit={cuit}
            hideInfo={false}
            balanceData={balanceData}
            postBalanceData={postBalanceData}
            isPdfMode
          />
        </Card>
      </div>
    );
  }
);

export default RiskAnalysisPdf;

